<template>
  <a-table
    class="table datatable thead-light dq-responsive-table"
    :columns="columns"
    :data-source="rows"
    :loading="loading"
    :pagination="false"
    row-key="id"
  >
    <template #bodyCell="{ column, record }">
      <template v-if="column.key === 'company_name'">
        <div class="d-flex align-items-center file-name-icon">
          <router-link
            :to="{ name: 'crm.companies.details', params: { id: record.id } }"
            class="avatar avatar-md border rounded-circle overflow-hidden d-inline-flex align-items-center justify-content-center bg-light text-dark"
          >
            <img
              v-if="record.logo_url"
              :src="record.logo_url"
              class="img-fluid"
              :alt="record.company_name"
            />
            <span v-else>{{ initials(record.company_name) }}</span>
          </router-link>
          <div class="ms-2">
            <h6 class="fw-medium mb-0">
              <router-link :to="{ name: 'crm.companies.details', params: { id: record.id } }">
                {{ record.company_name }}
              </router-link>
            </h6>
          </div>
        </div>
      </template>

      <template v-else-if="column.key === 'location'">
        <span>{{ record.location }}</span>
      </template>

      <template v-else-if="column.key === 'owners'">
        <span>{{ record.owners }}</span>
      </template>

      <template v-else-if="column.key === 'contact'">
        <ul class="contact-icon d-flex align-items-center">
          <li v-if="record.email">
            <a
              :href="`mailto:${record.email}`"
              class="p-1 rounded-circle contact-icon-mail d-flex align-items-center justify-content-center"
            >
              <span class="d-flex align-items-center justify-content-center">
                <i class="ti ti-mail text-gray-5"></i>
              </span>
            </a>
          </li>
          <li v-if="record.phone">
            <a
              :href="`tel:${record.phone}`"
              class="p-1 rounded-circle contact-icon-call d-flex align-items-center justify-content-center"
            >
              <span class="d-flex align-items-center justify-content-center">
                <i class="ti ti-phone-call text-gray-5"></i>
              </span>
            </a>
          </li>
          <li v-if="record.website_url">
            <a
              :href="normalizeUrl(record.website_url)"
              target="_blank"
              rel="noreferrer"
              class="p-1 rounded-circle contact-icon-skype d-flex align-items-center justify-content-center"
            >
              <span class="d-flex align-items-center justify-content-center">
                <i class="ti ti-world text-gray-5"></i>
              </span>
            </a>
          </li>
        </ul>
      </template>

      <template v-else-if="column.key === 'status'">
        <span :class="['badge', statusClass(record.status), 'd-inline-flex', 'align-items-center', 'badge-xs']">
          <i class="ti ti-point-filled me-1"></i>{{ record.status }}
        </span>
      </template>

      <template v-else-if="column.key === 'action'">
        <div class="action-icon d-inline-flex">
          <router-link :to="{ name: 'crm.companies.details', params: { id: record.id } }" class="me-2">
            <i class="ti ti-eye"></i>
          </router-link>
        </div>
      </template>
    </template>
  </a-table>
</template>

<style scoped>
@media (max-width: 767.98px) {
  :deep(.dq-responsive-table .ant-table) {
    min-width: 920px;
  }

  :deep(.dq-responsive-table .ant-table-cell) {
    vertical-align: top;
    white-space: normal;
  }
}
</style>

<script setup>
const props = defineProps({
  rows: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const columns = [
  { title: "Company Name", dataIndex: "company_name", key: "company_name" },
  { title: "Email", dataIndex: "email", key: "email" },
  { title: "Phone", dataIndex: "phone", key: "phone" },
  { title: "Location", dataIndex: "location", key: "location" },
  { title: "Owners", dataIndex: "owners", key: "owners" },
  { title: "Contact", dataIndex: "contact", key: "contact" },
  { title: "Status", dataIndex: "status", key: "status" },
  { title: "", key: "action" },
];

function initials(value = "") {
  return String(value)
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("") || "CO";
}

function statusClass(status) {
  switch ((status || "").toLowerCase()) {
    case "active":
      return "badge-success";
    case "suspended":
      return "badge-warning";
    case "inactive":
      return "badge-danger";
    default:
      return "badge-secondary-transparent";
  }
}

function normalizeUrl(value) {
  if (!value) return "#";
  return /^https?:\/\//i.test(value) ? value : `https://${value}`;
}
</script>
